'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import AppLogo from '@/components/ui/AppLogo';
import { Eye, EyeOff, Loader2, AlertCircle } from 'lucide-react';
import { Shield } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import ThemeToggle from '@/components/ui/ThemeToggle';

type LoginForm = {
  email: string;
  password: string;
  remember: boolean;
};

function destinationForProfile(p: {
  role: string;
  must_change_password: boolean;
  profile_completed: boolean;
}) {
  if (p.role === 'admin') return '/admin-dashboard';
  if (p.must_change_password) return '/change-password';
  if (p.role === 'investor') return '/investor-dashboard';
  if (!p.profile_completed) return '/complete-profile';
  return '/client-portal-dashboard';
}

export default function LoginPanel() {
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const { signIn, profile, loading } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>();

  // Leave login only when an auth cookie exists (middleware can see it).
  // Avoids a redirect loop if session is localStorage-only.
  useEffect(() => {
    if (loading || !profile) return;
    const hasAuthCookie = document.cookie.split(';').some((c) =>
      c.trim().includes('auth-token')
    );
    if (!hasAuthCookie) return;
    window.location.assign(destinationForProfile(profile));
  }, [loading, profile]);

  const onSubmit = async (data: LoginForm) => {
    setError('');
    try {
      const result = await signIn(data.email, data.password);
      const p = result?.profile;

      if (!p) {
        toast.error('Unable to load your profile. Please try again.');
        return;
      }

      toast.success('Signed in successfully');
      // Full navigation so the request carries auth cookies to middleware
      window.location.assign(destinationForProfile(p));
    } catch (err: any) {
      const msg = err?.message || 'Invalid email or password';
      setError(msg);
      toast.error(msg);
    }
  };

  return (
    <div className="flex-1 flex items-center justify-center px-6 py-12 bg-background">
      <div className="w-full max-w-md">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <AppLogo src="/assets/images/opclogo-1784542402159.png" size={40} />
            <span className="font-bold text-lg text-foreground">Operator Circle</span>
          </div>
          <ThemeToggle />
        </div>

        {/* Desktop theme toggle */}
        <div className="hidden lg:flex justify-end mb-4">
          <ThemeToggle />
        </div>

        <div className="mb-8">
          <h1 className="text-2xl font-bold text-foreground mb-1">Sign In to Portal</h1>
          <p className="text-muted-foreground text-sm">Access your secure Operator Circle account</p>
        </div>

        {/* Invitation notice */}
        <div className="flex items-center gap-2 bg-secondary border border-border rounded-lg px-4 py-3 mb-7">
          <Shield size={14} className="text-gold flex-shrink-0" />
          <p className="text-muted-foreground text-xs">
            This portal is for approved members only. Access is by invitation.{' '}
            <Link href="/request-access-page" className="text-accent hover:underline font-medium">
              Request access
            </Link>
          </p>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-danger-bg border border-danger/20 rounded-lg px-4 py-3 mb-5">
            <AlertCircle size={14} className="text-danger flex-shrink-0" />
            <p className="text-danger text-xs">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">
              Email Address
            </label>
            <input
              type="email"
              {...register('email', {
                required: 'Email is required',
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
              })}
              placeholder="your@email.com"
              className="input-field"
              autoComplete="email"
            />
            {errors.email && <p className="text-danger text-xs mt-1">{errors.email.message}</p>}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-semibold text-foreground">Password</label>
              <Link href="/forgot-password" className="text-accent text-xs hover:underline font-medium">
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                {...register('password', { required: 'Password is required' })}
                placeholder="Enter your password"
                className="input-field pr-10"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.password && <p className="text-danger text-xs mt-1">{errors.password.message}</p>}
          </div>

          <div className="flex items-center gap-2">
            <input
              id="remember"
              type="checkbox"
              {...register('remember')}
              className="w-4 h-4 rounded border-border accent-accent"
            />
            <label htmlFor="remember" className="text-sm text-muted-foreground cursor-pointer">
              Remember me for 30 days
            </label>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary w-full py-3.5 mt-1"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 size={16} className="animate-spin" />
                Authenticating...
              </span>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        <p className="text-center text-muted-foreground text-xs mt-6">
          Don&apos;t have access?{' '}
          <Link href="/request-access-page" className="text-accent hover:underline font-medium">
            Submit an access request
          </Link>
        </p>
      </div>
    </div>
  );
}